// Global Instructions Rule Applied!
// Detail shape of GET /distributions/:id and the wf__ distribution writers
// (wf__distribution_detail): header + sources + per-commitment lines + the
// linked Distribution fund transaction + totals; the compute / issue actions
// add their own envelope keys (docs/WATERFALL_CARRY_DESIGN.md, phase 3).
// Keys are raw column names (to_jsonb of the row) plus joined labels, so the
// row schemas are passthrough while pinning the fields the form consumes.
// The pivot of phase 6 PR 2b - `partners` (the Allocation grid, one entry per
// commitment) and `tiers` (the Tiers strip) - is built key by key in SQL
// (wf__distribution_partners / wf__distribution_tiers), so every object in
// it is strict. Ids are plain strings: funds and accounts carry Dataverse
// GUIDs that fail zod's uuid check.
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

// one SBA gate as Compute found it (SBIC LPA §7.01(a); owner answer 5: warn
// and record the override, never refuse on a data gap)
export const DistributionSbaCheckSchema = z
  .object({
    code: z.string(),
    label: z.string().nullish(),
    passed: z.boolean(),
    detail: z.string().nullish(),
    figures: z.record(z.string(), z.any()).nullish()
  })
  .passthrough()

// ── a consolidated distribution (phase 5) ────────────────────────────────────
// A consolidated fund's distribution is a header (fund_group_distributions)
// with ONE distributions row per legal entity; the LEAD row is the primary
// fund's and holds the inputs, the satellite entity rows are derived. The
// detail's prog__consolidated names the header and every entity; it is null
// on a distribution of a single fund. Built key by key in SQL
// (wf__distribution_consolidated), so the objects are strict.
export const DistributionConsolidatedEntitySchema = z
  .object({
    funds__id: z.string(),
    funds__name: z.string().nullish(),
    distributions__id: z.string(),
    is_lead: z.boolean(),
    gross_amount: z.number().nullish(),
    // this entity's part of the proceeds and its share of the run (from
    // the snapshot: null before Compute)
    entity_gross: z.number().nullish(),
    share: z.number().nullish(),
    needs_compute: z.boolean().nullish(),
    status_label: z.string().nullish(),
    prog__effective_total: z.number().nullish(),
    prog__sba_failed: z.boolean().nullish(),
    transaction_status_label: z.string().nullish()
  })
  .strict()

export const DistributionConsolidatedSchema = z
  .object({
    fund_group_distributions__id: z.string(),
    fund_groups__id: z.string(),
    fund_groups__name: z.string().nullish(),
    status_label: z.string().nullish(),
    gross_amount: z.number().nullish(),
    needs_compute: z.boolean().nullish(),
    // this row is the lead (the primary fund's): the inputs and the lifecycle live here
    is_lead: z.boolean(),
    lead_distribution_id: z.string().nullish(),
    entity_gross: z.number().nullish(),
    share: z.number().nullish(),
    entities: z.array(DistributionConsolidatedEntitySchema)
  })
  .strict()

export type DistributionConsolidatedEntity = z.infer<typeof DistributionConsolidatedEntitySchema>
export type DistributionConsolidated = z.infer<typeof DistributionConsolidatedSchema>

export const DistributionHeaderSchema = z
  .object({
    id: z.string(),
    fund_id: z.string(),
    name: z.string().nullish(),
    distribution_date: z.string().nullish(),
    status_id: z.number().nullish(),
    kind_id: z.number().nullish(),
    gross_amount: z.number().nullish(),
    fund_group_distribution_id: z.string().nullish(),
    needs_compute: z.boolean(),
    computed_at: z.string().nullish(),
    terms_snapshot: z.record(z.string(), z.any()).nullish(),
    realized_basis: z.record(z.string(), z.any()).nullish(),
    sba_checks: z.array(DistributionSbaCheckSchema).nullish(),
    sba_override_reason: z.string().nullish(),
    trashed: z.boolean().nullish(),
    fund_name: z.string().nullish(),
    functional_currency_id: z.string().nullish(),
    currency_symbol: z.string().nullish(),
    currency_code: z.string().nullish(),
    fund_is_sbic: z.boolean().nullish(),
    status_label: z.string().nullish(),
    kind_code: z.string().nullish(),
    kind_label: z.string().nullish(),
    prog__has_waterfall: z.boolean().nullish(),
    prog__scope_label: z.string().nullish(),
    prog__settles_by_special_distribution: z.boolean().nullish(),
    prog__sba_failed: z.boolean().nullish(),
    // what the amounts were computed under, from the snapshot (null before
    // the first Compute): how earlier distributions were checked, the basis
    // the tiers were tested on, the scope, the tolerance of the corrections
    prog__history_check: z.string().nullish(),
    prog__history_check_label: z.string().nullish(),
    prog__basis: z.string().nullish(),
    prog__basis_label: z.string().nullish(),
    prog__scope: z.string().nullish(),
    prog__correction_tolerance: z.number().nullish(),
    // a consolidated distribution's entity row (phase 5); null otherwise
    prog__consolidated: DistributionConsolidatedSchema.nullish()
  })
  .passthrough()

export const DistributionItemSchema = z
  .object({
    id: z.string(),
    distribution_id: z.string(),
    asset_transaction_id: z.string().nullish(),
    amount: z.number(),
    description: z.string().nullish(),
    display_order: z.number().nullish(),
    sharing: z.record(z.string(), z.any()).nullish(),
    asset_transactions__transaction_date: z.string().nullish(),
    asset_transactions__amount: z.number().nullish(),
    accounts__name: z.string().nullish(),
    securities__name: z.string().nullish(),
    asset_transaction_type_label: z.string().nullish(),
    // a waterfall with pools (phase 6 PR 3): the source's pool, derived from
    // its investment
    prog__pool_id: z.string().nullish(),
    prog__pool_code: z.string().nullish()
  })
  .passthrough()

export const DistributionPerCommitmentSchema = z
  .object({
    id: z.string(),
    distribution_id: z.string(),
    fund_commitment_id: z.string(),
    tier_id: z.string().nullish(),
    recipient_class_id: z.number(),
    calculated_amount: z.number().nullish(),
    override_amount: z.number().nullish(),
    is_return_of_capital: z.boolean(),
    rate_applied: z.number().nullish(),
    // 'tier' | 'own_share' | 'priority'
    kind: z.string(),
    // 'special_distribution' | 'sba' | 'lender_repayment' on a priority line
    priority_kind: z.string().nullish(),
    asset_transaction_id: z.string().nullish(),
    investors__id: z.string().nullish(),
    investors__name: z.string().nullish(),
    fund_commitments__amount: z.number().nullish(),
    fund_commitments__is_gp_commitment: z.boolean().nullish(),
    tier_sequence: z.number().nullish(),
    tier_type_label: z.string().nullish(),
    recipient_class_label: z.string().nullish(),
    prog__effective_amount: z.number().nullish(),
    // the cumulative rule (phase 6): the tier-by-tier figure, the partner's
    // entitlement to date and what it had received to date under the row's
    // tier and class, and the part of the correction left for a later
    // distribution; null on a priority line and on a row computed earlier
    incremental_amount: z.number().nullish(),
    entitled_cumulative: z.number().nullish(),
    received_to_date: z.number().nullish(),
    correction_carried: z.number().nullish(),
    // calculated - incremental: the Correction column
    prog__correction: z.number().nullish(),
    // a waterfall with pools: the pool the line was paid from and, on a
    // cross-pool line, the pool whose capital it returns
    pool_id: z.string().nullish(),
    remedied_pool_id: z.string().nullish(),
    pool_code: z.string().nullish(),
    remedied_pool_code: z.string().nullish()
  })
  .passthrough()

export const DistributionTransactionSchema = z
  .object({
    id: z.string(),
    status_label: z.string().nullish(),
    transaction_date: z.string().nullish(),
    journal_count: z.number().nullish(),
    total_debits: z.number().nullish()
  })
  .passthrough()

export const DistributionTotalsSchema = z
  .object({
    gross: z.number().nullish(),
    lp: z.number(),
    gp_own_share: z.number(),
    carry: z.number(),
    return_of_capital: z.number(),
    priority: z.number(),
    effective_total: z.number(),
    residual: z.number(),
    line_count: z.number()
  })
  .passthrough()

// ── the Allocation grid and the Tiers strip (phase 6 PR 2b) ──────────────────
// the bucket of a tier after this distribution; null where the snapshot holds
// no tier tests (the distribution was computed before they were kept)
export const DistributionTierStateSchema = z.enum(['full', 'open', 'not_reached', 'none'])

// a partner's cell under one tier
export const DistributionTierCellSchema = z
  .object({
    tier_id: z.string(),
    tier_sequence: z.number(),
    tier_type_label: z.string().nullish(),
    prog__pays_lp: z.boolean(),
    prog__pays_gp: z.boolean(),
    partner_calculated: z.number(),
    partner_effective: z.number(),
    carry_calculated: z.number(),
    carry_effective: z.number(),
    rate_lp: z.number().nullish(),
    rate_gp: z.number().nullish(),
    state_after: DistributionTierStateSchema.nullish(),
    remaining: z.number().nullish()
  })
  .strict()

// the cash to the partner, or the carry its profit bore: one override per
// line. recipient_class is the key the save's partner_overrides takes (the
// GP commitment's cash is its own-share rows, whose class is GP).
export const DistributionPartnerLineSchema = z
  .object({
    recipient_class: z.enum(['LP', 'GP']),
    calculated: z.number(),
    correction: z.number(),
    carried: z.number(),
    overridden: z.boolean(),
    override: z.number().nullish(),
    effective: z.number()
  })
  .strict()

// what a Cross-Pool Loss Offset tier found and remedied in one other pool
export const DistributionRemediedTestSchema = z
  .object({
    pool_id: z.string(),
    pool_code: z.string().nullish(),
    unremedied: z.number(),
    amount: z.number()
  })
  .strict()

// what a distribution remedied in (or received from) one pool
export const DistributionRemediedSchema = z
  .object({
    pool_id: z.string(),
    pool_code: z.string().nullish(),
    amount: z.number()
  })
  .strict()

// one tier test of a partner (per source on a Deal-by-Deal waterfall), with
// the cumulative figures of its rows per class
export const DistributionPartnerTestSchema = z
  .object({
    tier_id: z.string(),
    tier_sequence: z.number(),
    tier_type_label: z.string().nullish(),
    asset_transaction_id: z.string().nullish(),
    source_label: z.string().nullish(),
    // a waterfall with pools: the pool of the source; the Cross-Pool Loss
    // Offset tier's test has metric 'Loss Offset' and lists what it remedied
    pool_id: z.string().nullish(),
    pool_code: z.string().nullish(),
    remedied: z.array(DistributionRemediedTestSchema),
    metric: z.string(),
    basis: z.string().nullish(),
    rate_lp: z.number().nullish(),
    rate_gp: z.number().nullish(),
    s: z.number().nullish(),
    threshold: z.number().nullish(),
    base_amount: z.number().nullish(),
    target: z.number().nullish(),
    received_before: z.number().nullish(),
    headroom: z.number().nullish(),
    catch_up: z.number().nullish(),
    catch_up_received: z.number().nullish(),
    catch_up_total: z.number().nullish(),
    r: z.number().nullish(),
    weight: z.number().nullish(),
    remaining_before: z.number().nullish(),
    reached: z.boolean(),
    absorbed: z.number(),
    partner: z.number(),
    carry: z.number(),
    remaining: z.number().nullish(),
    state_after: DistributionTierStateSchema,
    calculated_lp: z.number().nullish(),
    incremental_lp: z.number().nullish(),
    entitled_lp: z.number().nullish(),
    received_lp: z.number().nullish(),
    correction_lp: z.number().nullish(),
    carried_lp: z.number().nullish(),
    calculated_gp: z.number().nullish(),
    incremental_gp: z.number().nullish(),
    entitled_gp: z.number().nullish(),
    received_gp: z.number().nullish(),
    correction_gp: z.number().nullish(),
    carried_gp: z.number().nullish()
  })
  .strict()

export const DistributionPartnerSourceSchema = z
  .object({
    asset_transaction_id: z.string(),
    label: z.string().nullish(),
    partner_effective: z.number(),
    carry_effective: z.number(),
    tiers: z.array(
      z.object({ tier_sequence: z.number(), partner_effective: z.number(), carry_effective: z.number() }).strict()
    )
  })
  .strict()

// one row of the grid: a commitment with a line on the distribution
export const DistributionPartnerSchema = z
  .object({
    fund_commitment_id: z.string(),
    investors__id: z.string().nullish(),
    investors__name: z.string().nullish(),
    fund_commitments__amount: z.number().nullish(),
    fund_commitments__is_gp_commitment: z.boolean(),
    prog__share: z.number(),
    prog__taken_first: z.number(),
    tiers: z.array(DistributionTierCellSchema),
    // null where the partner has no row of the line: the GP commitment bears
    // no carry; a partner paid by a priority line only has no tier row
    to_partner: DistributionPartnerLineSchema.nullish(),
    carry: DistributionPartnerLineSchema.nullish(),
    tests: z.array(DistributionPartnerTestSchema),
    sources: z.array(DistributionPartnerSourceSchema)
  })
  .strict()

// one line of the Tiers strip: the tier as the agreement states it, what
// this distribution puts under it and the test the run made
export const DistributionTierStripSchema = z
  .object({
    tier_id: z.string(),
    tier_sequence: z.number(),
    tier_type_label: z.string().nullish(),
    metric: z.string(),
    base: z.string().nullish(),
    threshold_source: z.string().nullish(),
    threshold_source_label: z.string().nullish(),
    stop_class: z.string().nullish(),
    stop_threshold: z.number().nullish(),
    stop_tier_group: z.string().nullish(),
    splits: z.array(
      z
        .object({
          class: z.string(),
          source: z.string().nullish(),
          source_label: z.string().nullish(),
          split_percent: z.number().nullish()
        })
        .strict()
    ),
    basis: z.string().nullish(),
    prog__pays_lp: z.boolean(),
    prog__pays_gp: z.boolean(),
    target: z.number().nullish(),
    received_before: z.number().nullish(),
    headroom: z.number().nullish(),
    catch_up: z.number().nullish(),
    catch_up_received: z.number().nullish(),
    class_shortfall: z.number().nullish(),
    s_eff: z.number().nullish(),
    class_amount: z.number().nullish(),
    this_distribution: z.number(),
    to_partners: z.number(),
    carry: z.number(),
    state_after: DistributionTierStateSchema.nullish(),
    remaining: z.number().nullish(),
    // the Cross-Pool Loss Offset tier: what this distribution remedied, per pool
    prog__is_loss_offset: z.boolean(),
    // the pools that tier offsets against, by code; none = every other pool
    offsets_pools: z.array(z.string()),
    remedied: z.array(DistributionRemediedSchema)
  })
  .strict()

// the Pools table (a waterfall with pools): one entry per pool the
// distribution touches - "before" from the pool results Compute froze, the
// rest from the rows, effective
export const DistributionPoolSchema = z
  .object({
    pool_id: z.string(),
    pool_code: z.string().nullish(),
    // the pool's capital (the target of its return of capital) and what of it
    // had been returned before this distribution
    contributions: z.number(),
    returned_before: z.number(),
    // a cell per tier of the definition: what the pool's proceeds pay under it
    tiers: z.array(
      z.object({ tier_sequence: z.number(), partner_effective: z.number(), carry_effective: z.number() }).strict()
    ),
    capital_due_before: z.number(),
    unremedied_loss_before: z.number(),
    proceeds: z.number(),
    returned: z.number(),
    remedied_out: z.number(),
    remedied_out_to: z.array(DistributionRemediedSchema),
    remedied_in: z.number(),
    remedied_in_from: z.array(DistributionRemediedSchema),
    gain: z.number(),
    carry: z.number(),
    capital_due_after: z.number()
  })
  .strict()

export const DistributionDetailSchema = z
  .object({
    distribution: DistributionHeaderSchema,
    items: z.array(DistributionItemSchema),
    per_commitment: z.array(DistributionPerCommitmentSchema),
    transaction: DistributionTransactionSchema.nullish(),
    totals: DistributionTotalsSchema,
    // the pivot the grid and the audit workbook read
    partners: z.array(DistributionPartnerSchema),
    tiers: z.array(DistributionTierStripSchema),
    // the Pools table of a waterfall with pools; [] otherwise
    pools: z.array(DistributionPoolSchema),
    // the action envelopes
    status: z.string().nullish(),
    status_message: z.string().nullish(),
    compute: z.record(z.string(), z.any()).nullish(),
    issue: z.record(z.string(), z.any()).nullish()
  })
  .strict()

export type DistributionSbaCheck = z.infer<typeof DistributionSbaCheckSchema>
export type DistributionHeader = z.infer<typeof DistributionHeaderSchema>
export type DistributionItem = z.infer<typeof DistributionItemSchema>
export type DistributionPerCommitment = z.infer<typeof DistributionPerCommitmentSchema>
export type DistributionTransaction = z.infer<typeof DistributionTransactionSchema>
export type DistributionTotals = z.infer<typeof DistributionTotalsSchema>
export type DistributionTierState = z.infer<typeof DistributionTierStateSchema>
export type DistributionTierCell = z.infer<typeof DistributionTierCellSchema>
export type DistributionPartnerLine = z.infer<typeof DistributionPartnerLineSchema>
export type DistributionPartnerTest = z.infer<typeof DistributionPartnerTestSchema>
export type DistributionPartnerSource = z.infer<typeof DistributionPartnerSourceSchema>
export type DistributionPartner = z.infer<typeof DistributionPartnerSchema>
export type DistributionTierStrip = z.infer<typeof DistributionTierStripSchema>
export type DistributionRemedied = z.infer<typeof DistributionRemediedSchema>
export type DistributionRemediedTest = z.infer<typeof DistributionRemediedTestSchema>
export type DistributionPool = z.infer<typeof DistributionPoolSchema>
export type DistributionDetail = z.infer<typeof DistributionDetailSchema>

export function validateDistributionDetail(data: unknown): DistributionDetail {
  try {
    return validateWithZodSchema(DistributionDetailSchema, data, 'distribution detail')
  } catch (error) {
    throw new Error(`Distribution detail validation failed: ${error}`)
  }
}

/** Save payload sent to POST /distributions (wf__save_distribution). */
export interface DistributionSavePayload {
  id?: string
  fund_id?: string
  name?: string | null
  distribution_date?: string | null
  kind_id?: number | null
  gross_amount?: number | null
  realized_basis?: Record<string, number> | null
  sba_override_reason?: string | null
  /** the replacement set of sources */
  items?: Array<{
    id?: string
    asset_transaction_id?: string | null
    amount: number
    description?: string | null
    display_order?: number | null
    sharing?: Record<string, number> | null
  }>
  /** targeted per-commitment overrides by row id (the priority lines): null clears one */
  per_commitment_overrides?: Array<{ id: string; override_amount: number | null }>
  /**
   * one override per partner and class - the cash to the partner, the carry
   * its profit bore. The server attributes it to the tier rows
   * (wf__attribute_partner_override); null clears the class's rows.
   */
  partner_overrides?: Array<{
    fund_commitment_id: string
    recipient_class: 'LP' | 'GP'
    override_amount: number | null
  }>
}

/**
 * Save payload sent to POST /fund-groups/distributions (fgd__save): the
 * single-fund inputs on the LEAD row - no overrides in this phase (the server
 * refuses them) - with the consolidated fund to create under, or the header
 * to edit.
 */
export type FundGroupDistributionSavePayload = Omit<
  DistributionSavePayload,
  'id' | 'fund_id' | 'per_commitment_overrides' | 'partner_overrides'
> &
  ({ fund_group_id: string; fund_group_distribution_id?: undefined } | { fund_group_distribution_id: string; fund_group_id?: undefined })

// the list rows of a fund's Distributions tab (wf__fund_distributions)
export const DistributionListRowSchema = z
  .object({
    distributions__id: z.string(),
    distributions__name: z.string().nullish(),
    distributions__distribution_date: z.string().nullish(),
    distributions__distribution_date_fmt: z.string().nullish(),
    funds__id: z.string().nullish(),
    funds__name: z.string().nullish(),
    cfg_distribution_statuses__label: z.string().nullish(),
    cfg_distribution_kinds__code: z.string().nullish(),
    cfg_distribution_kinds__label: z.string().nullish(),
    distributions__gross_amount: z.number().nullish(),
    distributions__gross_amount_fmt: z.string().nullish(),
    distributions__needs_compute: z.boolean().nullish(),
    prog__effective_total: z.number().nullish(),
    prog__effective_total_fmt: z.string().nullish(),
    prog__line_count: z.number().nullish(),
    fund_transactions__id: z.string().nullish(),
    cfg_transaction_statuses__label: z.string().nullish(),
    // a consolidated distribution's entity row (phase 5): the header, its
    // consolidated fund, and whether this is its lead (the primary fund's)
    fund_group_distributions__id: z.string().nullish(),
    fund_groups__name: z.string().nullish(),
    prog__consolidated_role: z.enum(['lead', 'entity']).nullish()
  })
  .strict()

export type DistributionListRow = z.infer<typeof DistributionListRowSchema>

export function validateDistributionListRows(data: unknown): DistributionListRow[] {
  try {
    return validateWithZodSchema(z.array(DistributionListRowSchema), data, 'distribution list rows')
  } catch (error) {
    throw new Error(`Distribution list validation failed: ${error}`)
  }
}

// a Booked realization a distribution can name as its source
// (wf__distribution_source_options)
export const DistributionSourceOptionSchema = z
  .object({
    value: z.string(),
    label: z.string(),
    transaction_date: z.string().nullish(),
    amount: z.number().nullish(),
    amount_fmt: z.string().nullish(),
    company_name: z.string().nullish(),
    security_name: z.string().nullish(),
    type_label: z.string().nullish(),
    // a waterfall with pools: the pool the source belongs to (null = none)
    pool_code: z.string().nullish(),
    // the options of a consolidated fund (phase 5): the member fund that
    // holds the realization (its name already ends the label)
    funds__id: z.string().nullish(),
    funds__name: z.string().nullish()
  })
  .strict()

export type DistributionSourceOption = z.infer<typeof DistributionSourceOptionSchema>

export function validateDistributionSourceOptions(data: unknown): DistributionSourceOption[] {
  try {
    return validateWithZodSchema(z.array(DistributionSourceOptionSchema), data, 'distribution source options')
  } catch (error) {
    throw new Error(`Distribution source options validation failed: ${error}`)
  }
}
